/**
 * Root router. Four branches:
 *
 *   1. authStore.isAuthenticated → /(tabs) (or /(onboarding)/name if
 *      first_name / email haven't been captured — legacy fallback for
 *      pre-redesign PIN-only signups).
 *   2. pendingReferral resolves to an active link AND its deal is
 *      currently visible → redirect to /<slug>?r=<token> so the recipient
 *      sees the canonical public deal-landing (athlete-bg + Head/OG meta
 *      + deal-aware SignupCard). Server-validated via referral_invites;
 *      stale tokens or archived deals fall through to (3)/(4).
 *   3. pendingReferral exists but has no resolvable deal (or it 404s) AND
 *      Platform.OS === 'web' → render <SignupCard deal={null} inviter={null}>
 *      inline so cold web visitors at app.marketpryce.com/ have somewhere
 *      to land.
 *   4. No pendingReferral AND iOS native → /(auth)/login (historical
 *      default — user types phone, returns via PIN).
 *
 * Loading state required while branch (2) hits the network — never flash
 * login/signup before we know which surface to render.
 */
import { Redirect, useLocalSearchParams } from 'expo-router';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { ActivityIndicator, Platform, StyleSheet, View } from 'react-native';

import { BrandedAuthShell } from '@/components/BrandedAuthShell';
import { SignupCard } from '@/components/SignupCard';
import { colors } from '@/constants/theme';
import { getPendingReferral } from '@/lib/pendingReferral';
import { publicSignupService } from '@/services/publicSignup';
import { authStore } from '@/stores/auth.store';

type Resolution =
  | { kind: 'loading' }
  | { kind: 'landing'; slug: string; token: string }
  | { kind: 'no_referral' };

const Index = observer(() => {
  // Prefer the URL's `?r=` token over the persisted pendingReferral so
  // freshly-clicked legacy `/?r=token` URLs resolve their deal on the
  // very first render — even before `captureInitialReferral` (which
  // runs from `_layout.tsx#useEffect`) has flushed it to secureStore.
  // Without this the redirect to `/{slug}?r=token` would race the
  // localStorage write and the recipient would briefly land on the
  // generic SignupCard before the redirect fires (or never see the
  // redirect at all if the store hadn't loaded yet).
  const params = useLocalSearchParams<{ r?: string }>();
  const urlToken = typeof params.r === 'string' ? params.r : undefined;

  const [referralResolution, setReferralResolution] = useState<Resolution>({ kind: 'loading' });

  useEffect(() => {
    if (authStore.isAuthenticated) return;
    let cancelled = false;
    (async () => {
      let token = urlToken;
      if (!token) {
        const referral = await getPendingReferral().catch(() => null);
        token = referral?.referralLinkToken;
      }
      if (!token) {
        if (!cancelled) setReferralResolution({ kind: 'no_referral' });
        return;
      }
      try {
        const invite = await publicSignupService.invite(token);
        if (cancelled) return;
        if (invite.deal) {
          setReferralResolution({ kind: 'landing', slug: invite.deal.slug, token });
        } else {
          setReferralResolution({ kind: 'no_referral' });
        }
      } catch {
        if (!cancelled) setReferralResolution({ kind: 'no_referral' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [urlToken]);

  if (authStore.isAuthenticated) {
    if (authStore.needsOnboarding) {
      return <Redirect href="/(onboarding)/name" />;
    }
    return <Redirect href="/(tabs)" />;
  }

  // Hold the splash until hydrate() resolves: the referral lookup
  // settles from local storage in milliseconds while hydrate() blocks
  // on the network, so without this an authed web user sees the public
  // SignupCard flash on every cold load of "/" before snapping home.
  if (!authStore.hydrated || referralResolution.kind === 'loading') {
    return (
      <View style={styles.splash}>
        <ActivityIndicator color={colors.electricGreen} size="large" />
      </View>
    );
  }

  if (referralResolution.kind === 'landing') {
    return (
      <Redirect
        href={{
          pathname: '/[slug]',
          params: { slug: referralResolution.slug, r: referralResolution.token },
        }}
      />
    );
  }

  // no_referral path. Web cold visitors get a real signup landing;
  // iOS native goes to login (the historical default — they typed
  // their phone in to come back).
  if (Platform.OS === 'web') {
    return (
      <BrandedAuthShell>
        <SignupCard deal={null} inviter={null} />
      </BrandedAuthShell>
    );
  }
  return <Redirect href="/(auth)/login" />;
});

export default Index;

const styles = StyleSheet.create({
  splash: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.deepGreen,
  },
});
